import { ReadonlyMarkdown } from '@bangle.io/editor';
import { getShareMediaUrl, getSharePayload } from '@bangle.io/supabase';
import { resolveLocalMarkdownAsset } from '@bangle.io/ws-path';
import React, { useCallback, useEffect, useState } from 'react';
import { ReaderPropertiesPanel } from './reader-properties';
import { ReaderThemeToggle } from './reader-theme-toggle';

function resolveShareAssetFilePath(
  itemPath: string,
  markdownSrc: string,
): string {
  const resolved = resolveLocalMarkdownAsset(`share:${itemPath}`, markdownSrc);
  if (resolved?.path) return resolved.path;
  return markdownSrc.trim().replace(/^\//, '');
}

export function ReaderPage({ token }: { token: string }) {
  const [title, setTitle] = useState('Shared note');
  const [itemPath, setItemPath] = useState('');
  const [markdown, setMarkdown] = useState<string | null>(null);
  const [allowCopy, setAllowCopy] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const resolveImageSrc = useCallback(
    (src: string) =>
      getShareMediaUrl(token, resolveShareAssetFilePath(itemPath, src)),
    [token, itemPath],
  );

  useEffect(() => {
    void (async () => {
      try {
        const payload = await getSharePayload(token);
        setTitle(payload.title || 'Shared note');
        setItemPath(payload.item_path || '');
        setMarkdown(payload.markdown || '');
        setAllowCopy(payload.allow_copy);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Link unavailable');
      }
    })();
  }, [token]);

  if (error) {
    return (
      <div className="sn-auth-shell">
        <div className="sn-auth-stage sn-auth-stage--compact">
          <section className="sn-auth-panel">
            <h1 className="sn-auth-brand sn-auth-brand--compact">
              {t.app.pageAuth.brand}
            </h1>
            <p className="sn-auth-error" role="alert">
              {error}
            </p>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="sn-reader-shell">
      <header className="sn-reader-header">
        <span className="sn-auth-brand sn-auth-brand--compact">
          {t.app.pageAuth.brand}
        </span>
        <div className="sn-reader-header-actions">
          <ReaderThemeToggle />
          <a href="/">Try Typo Notes</a>
        </div>
      </header>
      <article className="sn-reader-article" data-properties-panel="">
        <h1 className="sn-reader-title">{title}</h1>
        {markdown === null ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : (
          <>
            <ReaderPropertiesPanel markdown={markdown} />
            <ReadonlyMarkdown
              markdown={markdown}
              allowCopy={allowCopy}
              resolveImageSrc={resolveImageSrc}
              className="sn-reader-wysiwyg"
              showToc
            />
          </>
        )}
      </article>
    </div>
  );
}
